// public-reader/components/ReaderRoute.tsx — loads + renders one public
// book by slug. Reuses scenario theme tokens from the main app's
// `scenarios/presentation` so the public reader inherits the same
// aesthetic per scenario as the in-app BookReader.
//
// Layout: a PublicHeader at the top, then a two-pane reader (chapter
// sidebar + body) mirroring the in-app BookReader. The body has a
// scenario-themed background banner with the title + author + lineage
// attribution. Fork + Share + EPUB actions live in the chapter-end
// affordance.
//
// Phase C i18n: the reader is language-aware.
//   - Reads the visitor's preferred language from Phase A's preference
//     (signed-in users.language or anonymous localStorage["ember.lang"]).
//   - On book load, if the preference differs from the book's source
//     language, shows the TranslateBanner. Tapping it switches the active
//     reader language; the book re-fetches with `?lang=X` so chapter 1
//     ships translated inline and chapters 2..N return as ref-only.
//   - Chapter selection lazy-fetches the body via
//     `GET /:slug/chapters/:n?lang=X` when bodyMd is null.
//   - Speculative prefetch: scrolling past 50% of the current chapter
//     fires a fire-and-forget request for chapter N+1 so the server
//     caches the next translation before the reader navigates to it.
//     Gated on `navigator.connection.effectiveType` to skip prefetch on
//     non-`4g` connections (cellular-friendly).
//   - EPUB download in a non-original language fetches via Blob (so we
//     can show a "Translating chapters…" progress affordance) before
//     handing the file to the browser.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useTranslation } from "react-i18next";
import {
  loadPublicBook,
  buildShareUrl,
  buildEpubUrl,
  fetchPublicChapter,
  prefetchPublicChapter,
  downloadEpubBlob,
  PublicBooksApiError,
  type PublicBookResponse,
  type PublicBookChapter,
} from "../services/publicBooksApi";
import { SCENARIO_PRESENTATIONS } from "../../scenarios/presentation";
import {
  loadAnonymousLanguage,
  detectBrowserLanguage,
} from "../../i18n/detectLanguage";
import {
  findSupportedLanguage,
  SUPPORTED_CODES,
} from "../../i18n/supportedLanguages";
import { loadLanguage } from "../../i18n";
import PublicHeader from "./PublicHeader";
import PublicFooter from "./PublicFooter";
import ShareSheet from "./ShareSheet";
import ForkAction from "./ForkAction";
import TranslateBanner from "./TranslateBanner";

const WWW_URL = "https://www.emberhollow.ai";

export interface ReaderRouteProps {
  slug: string;
}

/**
 * Resolve the visitor's preferred reading language for the public reader.
 *
 *   - Signed-in users carry their `users.language` over via Phase A's
 *     silent push to localStorage["ember.lang"] (i18next-browser-
 *     languagedetector mirrors it). So a single read of that key handles
 *     both signed-in + anonymous cases.
 *   - Falls back to `detectBrowserLanguage()` when storage is empty.
 *   - Returns null when nothing supported resolves (we then default to
 *     the book's source language without surfacing a banner).
 */
function resolveUserLanguage(): string | null {
  const stored = loadAnonymousLanguage();
  if (stored) return stored;
  return detectBrowserLanguage();
}

/**
 * Render markdown chapter body. Mirrors web/src/components/BookReader.tsx
 * — paragraph/heading split via blank lines and `#` prefixes. Keep the
 * two in sync.
 */
function renderMarkdownBody(body: string, fontFamily: string): JSX.Element[] {
  const blocks = body.split(/\n\s*\n/);
  return blocks.map((raw, i) => {
    const trimmed = raw.trim();
    if (!trimmed) return <div key={i} />;
    if (/^###\s+/.test(trimmed)) {
      return (
        <h4
          key={i}
          style={{
            margin: "1.4em 0 0.4em",
            fontFamily,
            fontSize: "1.1em",
            fontWeight: 600,
            letterSpacing: "0.02em",
          }}
        >
          {trimmed.replace(/^###\s+/, "")}
        </h4>
      );
    }
    if (/^##\s+/.test(trimmed)) {
      return (
        <h3
          key={i}
          style={{
            margin: "1.6em 0 0.5em",
            fontFamily,
            fontSize: "1.25em",
            fontWeight: 600,
            letterSpacing: "0.02em",
          }}
        >
          {trimmed.replace(/^##\s+/, "")}
        </h3>
      );
    }
    if (/^#\s+/.test(trimmed)) {
      return (
        <h2
          key={i}
          style={{
            margin: "1.8em 0 0.6em",
            fontFamily,
            fontSize: "1.4em",
            fontWeight: 500,
          }}
        >
          {trimmed.replace(/^#\s+/, "")}
        </h2>
      );
    }
    return (
      <p
        key={i}
        style={{
          margin: "0 0 1.1em",
          lineHeight: 1.75,
          fontFamily,
          fontSize: "1.08em",
        }}
      >
        {trimmed.split(/\n/).map((line, j, arr) => (
          <span key={j}>
            {line}
            {j < arr.length - 1 ? <br /> : null}
          </span>
        ))}
      </p>
    );
  });
}

/**
 * Lineage attribution string. Falls back gracefully on an empty chain
 * (no fork). Built once per render of the cover.
 */
function lineageText(book: PublicBookResponse): string | null {
  const lineage = book.lineage;
  if (!lineage || lineage.length === 0) return null;
  const immediate = lineage[0]!;
  return `Continued from chapter ${immediate.chapterNumber} of "${immediate.storyName}" by ${immediate.userName}`;
}

export default function ReaderRoute({ slug }: ReaderRouteProps): JSX.Element {
  const { t } = useTranslation(["public-reader"]);
  const [book, setBook] = useState<PublicBookResponse | null>(null);
  const [activeChapterId, setActiveChapterId] = useState<string | null>(null);
  // `activeLang` — the language the reader is CURRENTLY rendered in.
  // Null until the first book load resolves; thereafter mirrors the
  // server's `lang` field on the response.
  const [activeLang, setActiveLang] = useState<string | null>(null);
  // `userLang` — the visitor's preferred language, resolved once on
  // mount. Used to drive the TranslateBanner offer.
  const [userLang, setUserLang] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; code?: string } | null>(
    null,
  );
  const [shareOpen, setShareOpen] = useState(false);
  const [lineageExpanded, setLineageExpanded] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [bannerBusy, setBannerBusy] = useState(false);
  // Per-chapter body cache (keyed by chapter id). Hydrated from the
  // initial book load + filled by lazy fetches as the user navigates.
  const [chapterBodies, setChapterBodies] = useState<
    Record<string, { title: string; bodyMd: string } | undefined>
  >({});
  const [chapterLoadingId, setChapterLoadingId] = useState<string | null>(null);
  const [epubProgress, setEpubProgress] = useState<
    null | "downloading" | "translating"
  >(null);
  const mainRef = useRef<HTMLDivElement | null>(null);
  // Tracks which (chapterId, lang) pairs have been prefetched in this
  // session — avoids re-firing prefetch on every scroll event.
  const prefetchedRef = useRef<Set<string>>(new Set());
  // The language used on the LAST successful book fetch. Compared
  // against the next desired language so the load effect below skips
  // the harmless-but-wasteful duplicate fetch that would otherwise fire
  // after the initial mount (activeLang flips null → res.lang once the
  // server replies). Reset alongside the rest of the per-slug state.
  const fetchedLangRef = useRef<string | null>(null);

  // Resolve the visitor's preference once per mount. We don't react to
  // it changing — the TranslateBanner is a one-shot per session offer.
  useEffect(() => {
    setUserLang(resolveUserLanguage());
  }, []);

  /**
   * Hydrate the per-chapter cache from a book response. The server
   * inlines chapter 1's body when translating; non-null bodies on every
   * other chapter (the untranslated source path) all populate too.
   */
  const seedChapterCache = useCallback((res: PublicBookResponse) => {
    setChapterBodies((prev) => {
      const next = { ...prev };
      for (const c of res.chapters) {
        if (c.bodyMd !== null && c.bodyMd !== undefined) {
          next[c.id] = { title: c.title, bodyMd: c.bodyMd };
        }
      }
      return next;
    });
  }, []);

  // ── Reset everything when the slug changes ──────────────────────
  //
  // The slug effect resets state; the load effect below actually fires
  // the request. Splitting them lets the language-change re-fetch reuse
  // existing UI state (sidebar position, banner-dismissed flag, etc.)
  // rather than blowing it all away.
  useEffect(() => {
    setBook(null);
    setActiveChapterId(null);
    setActiveLang(null);
    setChapterBodies({});
    setBannerDismissed(false);
    prefetchedRef.current = new Set();
    fetchedLangRef.current = null;
  }, [slug]);

  // ── Load on slug or active-language change ──────────────────────
  useEffect(() => {
    // First mount per slug: activeLang is null, fetchedLangRef is null
    //   → fetch the source.
    // Banner-accept: activeLang is now userLang ≠ fetchedLangRef
    //   → fetch translated.
    // Post-fetch state catch-up: activeLang is now res.lang,
    // fetchedLangRef === res.lang → SKIP (this is the redundant case).
    if (fetchedLangRef.current !== null && fetchedLangRef.current === activeLang) {
      return;
    }
    // Language genuinely changing — drop any per-chapter bodies cached
    // in the previous language so the new language is served fresh.
    // The book response will re-seed chapter 1 inline and subsequent
    // chapters lazy-fetch.
    if (fetchedLangRef.current !== null && fetchedLangRef.current !== activeLang) {
      setChapterBodies({});
      prefetchedRef.current = new Set();
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await loadPublicBook(slug, activeLang ?? undefined);
        if (cancelled) return;
        fetchedLangRef.current = res.lang;
        setBook(res);
        setActiveChapterId((prev) =>
          // Preserve the active chapter across a language switch when
          // possible — keeps the reader's place.
          prev && res.chapters.some((c) => c.id === prev)
            ? prev
            : res.chapters[0]?.id ?? null,
        );
        setActiveLang(res.lang);
        seedChapterCache(res);
        // Best-effort: bump the meta-tag title so social previews + the
        // tab name use the book's title.
        if (typeof document !== "undefined") {
          document.title = `${res.title} · Ember Hollow`;
        }
        // Make sure the active i18n language matches the reader
        // language — banner / progress strings then come out in the
        // right locale. No-op when they already match.
        if (SUPPORTED_CODES.has(res.lang)) {
          void loadLanguage(res.lang);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof PublicBooksApiError) {
          setError({ message: err.message, code: err.code });
        } else {
          setError({
            message:
              err instanceof Error ? err.message : "Failed to load the book.",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // activeLang in deps so banner-accept triggers a re-fetch.
  }, [slug, activeLang, seedChapterCache]);

  // ── Lazy-load the active chapter's body when it's missing ────────
  //
  // The book response only ships chapter 1's body when a translation is
  // active. Selecting any other chapter triggers a fetch here.
  useEffect(() => {
    if (!book || !activeChapterId) return;
    if (chapterBodies[activeChapterId]) return;
    let cancelled = false;
    setChapterLoadingId(activeChapterId);
    const chapter = book.chapters.find((c) => c.id === activeChapterId);
    if (!chapter) return;
    (async () => {
      try {
        const res = await fetchPublicChapter(
          slug,
          chapter.chapterNumber,
          activeLang ?? undefined,
        );
        if (cancelled) return;
        setChapterBodies((prev) => ({
          ...prev,
          [chapter.id]: { title: res.title, bodyMd: res.bodyMd },
        }));
      } catch (err) {
        if (cancelled) return;
        // Surface a soft error — the chapter pane stays in its
        // loading state and the user can re-select to retry.
        console.warn(
          "[ReaderRoute] chapter fetch failed:",
          err instanceof Error ? err.message : err,
        );
      } finally {
        if (!cancelled) setChapterLoadingId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [book, activeChapterId, slug, activeLang, chapterBodies]);

  // ── Speculative prefetch of the NEXT chapter ─────────────────────
  //
  // When the reader scrolls past 50% of the current chapter, we fire a
  // fire-and-forget request for chapter N+1 in the active language so
  // the server caches the translation before the reader navigates to
  // it. Gated on `effectiveType === "4g"` (or undefined, e.g. iOS
  // Safari) to skip prefetch on slow/cellular connections.
  useEffect(() => {
    if (!book || !activeChapterId || !activeLang) return;
    // Same-language reads don't need prefetch — the source body is
    // already in the row we got from /:slug.
    if (activeLang === book.originalLang) return;

    const el = mainRef.current;
    if (!el) return;

    const handler = () => {
      const ec = (navigator as unknown as {
        connection?: { effectiveType?: string };
      }).connection?.effectiveType;
      if (ec && ec !== "4g") return; // slow connection — skip prefetch
      const ratio =
        el.scrollHeight > el.clientHeight
          ? (el.scrollTop + el.clientHeight) / el.scrollHeight
          : 1;
      if (ratio < 0.5) return;
      // Find the next chapter (by sidebar order — already sorted by
      // chapterNumber server-side).
      const idx = book.chapters.findIndex((c) => c.id === activeChapterId);
      if (idx < 0) return;
      const next = book.chapters[idx + 1];
      if (!next) return;
      // Skip if already cached locally or already prefetched this
      // session.
      if (chapterBodies[next.id]) return;
      const key = `${next.id}:${activeLang}`;
      if (prefetchedRef.current.has(key)) return;
      prefetchedRef.current.add(key);
      prefetchPublicChapter(slug, next.chapterNumber, activeLang);
    };

    el.addEventListener("scroll", handler, { passive: true });
    return () => el.removeEventListener("scroll", handler);
  }, [book, activeChapterId, activeLang, slug, chapterBodies]);

  // Banner-accept handler — switch the active reader language. Triggers
  // the load effect above to re-fetch the book.
  const acceptTranslation = useCallback(async () => {
    if (!userLang) return;
    setBannerBusy(true);
    setActiveLang(userLang);
    // Defensive: drop busy state shortly after — the load effect will
    // run and replace state. We don't await it directly because the
    // useEffect path is what drives the re-fetch.
    setTimeout(() => setBannerBusy(false), 300);
  }, [userLang]);

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true);
  }, []);

  // EPUB download — Phase C: when translating, route through the Blob
  // path so we can render a "Translating chapters…" affordance during
  // the (potentially multi-second) batched-translation call. When NOT
  // translating, fall through to the simple `<a download>` link.
  const downloadEpub = useCallback(async () => {
    if (!book) return;
    const lang = activeLang ?? book.originalLang;
    if (lang === book.originalLang) {
      // Plain GET — let the browser handle the file download via the
      // existing anchor href. The handler shouldn't fire in that case
      // (the JSX renders an <a>), but stay defensive.
      window.location.href = buildEpubUrl(slug);
      return;
    }
    setEpubProgress("downloading");
    // Surface the "translating" stage after a short delay — most
    // first-time fetches will be deep in batched-translation by then.
    const translatingTimer = setTimeout(
      () => setEpubProgress("translating"),
      400,
    );
    try {
      const { blob, filename } = await downloadEpubBlob(slug, lang);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn(
        "[ReaderRoute] EPUB download failed:",
        err instanceof Error ? err.message : err,
      );
    } finally {
      clearTimeout(translatingTimer);
      setEpubProgress(null);
    }
  }, [book, activeLang, slug]);

  // Theme tokens — defensive fallback for an unknown scenario.
  const presentation = book ? SCENARIO_PRESENTATIONS[book.scenario.id] : null;
  const theme = presentation?.theme;
  const fontFamily =
    theme?.fontFamily ??
    "'Cormorant Garamond', 'EB Garamond', 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif";
  const accent = theme?.accent ?? "#d4a574";
  const accentMuted = theme?.accentMuted ?? "rgba(212,165,116,0.45)";
  const ink = theme?.ink ?? "#e8d9b8";
  const inkMuted = theme?.inkMuted ?? "#8a7355";
  const paper = theme?.paper ?? "rgba(20,14,10,0.55)";
  const paperEdge = theme?.paperEdge ?? "rgba(212,165,116,0.18)";
  const bgGradient =
    theme?.bgGradient ??
    "radial-gradient(ellipse at top, #1a1410 0%, #0d0907 60%, #050302 100%)";

  const activeChapter = useMemo<PublicBookChapter | null>(
    () =>
      book?.chapters.find((c) => c.id === activeChapterId) ??
      book?.chapters[0] ??
      null,
    [book, activeChapterId],
  );

  // Reset scroll on chapter change.
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    queueMicrotask(() => {
      if (mainRef.current) mainRef.current.scrollTop = 0;
    });
  }, [activeChapterId]);

  const lineage = book ? lineageText(book) : null;
  const shareUrl = useMemo(() => buildShareUrl(slug), [slug]);

  const openShare = useCallback(() => setShareOpen(true), []);
  const closeShare = useCallback(() => setShareOpen(false), []);

  // ── Derived: should we offer the translation banner? ────────────
  const showTranslateBanner = Boolean(
    book &&
      userLang &&
      SUPPORTED_CODES.has(userLang) &&
      userLang !== book.originalLang &&
      // Don't offer if we're already serving in the user's language.
      activeLang === book.originalLang &&
      !bannerDismissed,
  );

  // ── Derived: render-ready (title, body) for the active chapter ─
  //
  // Returns the cached translated copy when present, otherwise falls
  // back to whatever the book payload carries (which may be a source
  // body, or null if the server is in translating mode and this isn't
  // chapter 1). Null body → render the "translating chapter N of M…"
  // affordance instead of markdown.
  const activeBody = useMemo<{ title: string; bodyMd: string } | null>(() => {
    if (!activeChapter) return null;
    const cached = chapterBodies[activeChapter.id];
    if (cached) return cached;
    if (
      typeof activeChapter.bodyMd === "string" &&
      activeChapter.bodyMd.length > 0
    ) {
      return { title: activeChapter.title, bodyMd: activeChapter.bodyMd };
    }
    return null;
  }, [activeChapter, chapterBodies]);

  // ── Styles ─────────────────────────────────────────────────────────
  const shell: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: bgGradient,
    color: ink,
    fontFamily,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };
  const main: CSSProperties = {
    display: "flex",
    flex: 1,
    minHeight: 0,
  };
  const sidebar: CSSProperties = {
    width: 280,
    flexShrink: 0,
    borderRight: `1px solid ${paperEdge}`,
    background: paper,
    overflowY: "auto",
    padding: "16px 0",
  };
  const sidebarTitle: CSSProperties = {
    padding: "0 20px 12px",
    fontSize: "0.7rem",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: inkMuted,
    fontFamily: "'JetBrains Mono', monospace",
  };
  const chapterListItem = (active: boolean): CSSProperties => ({
    padding: "10px 20px",
    cursor: "pointer",
    color: active ? accent : ink,
    background: active ? `${accentMuted}` : "transparent",
    borderLeft: active ? `3px solid ${accent}` : "3px solid transparent",
    fontSize: "0.92rem",
    transition: "background 0.18s, color 0.18s",
  });
  const chapterListLabel: CSSProperties = {
    fontSize: "0.65rem",
    fontFamily: "'JetBrains Mono', monospace",
    color: inkMuted,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: 4,
  };
  const body: CSSProperties = {
    flex: 1,
    minWidth: 0,
    overflowY: "auto",
    padding: "0",
    color: ink,
  };
  const bodyInner: CSSProperties = {
    maxWidth: 760,
    margin: "0 auto",
    padding: "56px 64px",
  };
  const titleStyle: CSSProperties = {
    margin: "0 0 8px",
    fontSize: "2.4rem",
    fontWeight: 500,
    color: accent,
    letterSpacing: "0.01em",
  };
  const authorStyle: CSSProperties = {
    margin: "0 0 6px",
    fontStyle: "italic",
    color: ink,
    opacity: 0.9,
    fontSize: "1.05rem",
  };
  const subtitleStyle: CSSProperties = {
    margin: "0 0 14px",
    color: inkMuted,
    fontSize: "0.95rem",
  };
  const lineageStyle: CSSProperties = {
    margin: "0 0 28px",
    color: inkMuted,
    fontSize: "0.82rem",
    fontStyle: "italic",
    cursor: book && book.lineage.length > 1 ? "pointer" : "default",
  };
  const lineageExpandedRow: CSSProperties = {
    marginLeft: 12,
    color: inkMuted,
    fontSize: "0.78rem",
    listStyle: "none",
    padding: 0,
  };
  const chapterLabel: CSSProperties = {
    fontSize: "0.7rem",
    fontFamily: "'JetBrains Mono', monospace",
    letterSpacing: "0.2em",
    textTransform: "uppercase",
    color: inkMuted,
    marginBottom: 8,
    marginTop: 32,
  };
  const chapterTitle: CSSProperties = {
    margin: "0 0 28px",
    fontFamily,
    fontSize: "1.9rem",
    fontWeight: 500,
    color: accent,
    letterSpacing: "0.01em",
  };
  const actions: CSSProperties = {
    display: "flex",
    gap: 12,
    marginTop: 32,
    paddingTop: 24,
    borderTop: `1px solid ${paperEdge}`,
    flexWrap: "wrap",
  };
  const secondaryAction: CSSProperties = {
    background: "transparent",
    color: inkMuted,
    border: `1px solid ${paperEdge}`,
    padding: "8px 16px",
    fontSize: "0.85rem",
    fontFamily: "inherit",
    cursor: "pointer",
    borderRadius: 2,
    textDecoration: "none",
    display: "inline-block",
  };
  const emptyState: CSSProperties = {
    padding: 64,
    color: inkMuted,
    fontStyle: "italic",
    textAlign: "center",
  };

  // ── Render branches ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={shell}>
        <PublicHeader
          accent={accent}
          inkMuted={inkMuted}
          paperEdge={paperEdge}
          paper={paper}
        />
        <div style={emptyState}>Loading…</div>
      </div>
    );
  }

  if (error || !book) {
    return (
      <div style={shell}>
        <PublicHeader
          accent={accent}
          inkMuted={inkMuted}
          paperEdge={paperEdge}
          paper={paper}
        />
        <div style={emptyState}>
          {error?.code === "not_found" || !book
            ? "This book isn't shared, or the link has been revoked."
            : (error?.message ?? "Failed to load the book.")}
          <div style={{ marginTop: 20 }}>
            <a href={WWW_URL} style={{ ...secondaryAction, color: accent, borderColor: accentMuted }}>
              Play at www.emberhollow.ai →
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Native names for badges + banner — falls back to the code when the
  // entry isn't in the supported list (defensive).
  const activeLangNative = activeLang
    ? findSupportedLanguage(activeLang)?.nativeName ?? activeLang
    : "";
  const originalLangNative =
    findSupportedLanguage(book.originalLang)?.nativeName ?? book.originalLang;
  const isTranslated = activeLang !== null && activeLang !== book.originalLang;

  const badgeStyle: CSSProperties = {
    display: "inline-block",
    padding: "2px 10px",
    marginLeft: 8,
    fontSize: "0.7rem",
    color: accent,
    border: `1px solid ${accentMuted}`,
    borderRadius: 999,
    letterSpacing: "0.04em",
    fontFamily: "'JetBrains Mono', monospace",
  };
  const translatingNote: CSSProperties = {
    padding: "20px 0",
    color: inkMuted,
    fontStyle: "italic",
    fontSize: "0.95rem",
  };
  const restoreOriginalBtn: CSSProperties = {
    background: "transparent",
    color: inkMuted,
    border: "none",
    padding: 0,
    fontSize: "0.78rem",
    fontFamily: "inherit",
    cursor: "pointer",
    textDecoration: "underline",
    marginLeft: 8,
  };
  const restoreOriginal = () => {
    setBannerDismissed(false);
    setActiveLang(book.originalLang);
  };

  return (
    <div style={shell}>
      <PublicHeader
        accent={accent}
        inkMuted={inkMuted}
        paperEdge={paperEdge}
        paper={paper}
      />

      {showTranslateBanner && userLang && (
        <TranslateBanner
          originalLang={book.originalLang}
          userLang={userLang}
          busy={bannerBusy}
          onAccept={() => {
            void acceptTranslation();
          }}
          onDismiss={dismissBanner}
          accent={accent}
          accentMuted={accentMuted}
          ink={ink}
          inkMuted={inkMuted}
          paper={paper}
          paperEdge={paperEdge}
        />
      )}

      {epubProgress && (
        <div style={translatingNote} role="status" aria-live="polite">
          {epubProgress === "translating"
            ? t("public-reader:epub.translating")
            : t("public-reader:epub.downloading")}
        </div>
      )}

      <div style={main}>
        <aside style={sidebar}>
          <div style={sidebarTitle}>chapters</div>
          {book.chapters.length === 0 && (
            <div
              style={{ padding: "0 20px", color: inkMuted, fontStyle: "italic" }}
            >
              No chapters yet.
            </div>
          )}
          {book.chapters.map((c) => (
            <div
              key={c.id}
              role="button"
              tabIndex={0}
              onClick={() => setActiveChapterId(c.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setActiveChapterId(c.id);
                }
              }}
              style={chapterListItem(c.id === activeChapterId)}
            >
              <div style={chapterListLabel}>chapter {c.chapterNumber}</div>
              <div>{c.title}</div>
            </div>
          ))}
        </aside>

        <main ref={mainRef} style={body}>
          <div style={bodyInner}>
            <h1 style={titleStyle}>{book.title}</h1>
            <p style={authorStyle}>by {book.author.name}</p>
            <p style={subtitleStyle}>
              {book.scenario.name}
              {book.customSubtitle ? ` · ${book.customSubtitle}` : ""}
            </p>
            {lineage && (
              <div>
                <div
                  style={lineageStyle}
                  onClick={() =>
                    book.lineage.length > 1 && setLineageExpanded((x) => !x)
                  }
                  role={book.lineage.length > 1 ? "button" : undefined}
                  tabIndex={book.lineage.length > 1 ? 0 : undefined}
                >
                  {lineage}
                  {book.lineage.length > 1 && (
                    <span style={{ marginLeft: 6 }}>
                      {lineageExpanded ? "▾ less" : "▸ full chain"}
                    </span>
                  )}
                </div>
                {lineageExpanded && (
                  <ul style={lineageExpandedRow}>
                    {book.lineage.slice(1).map((l) => (
                      <li key={l.storyId}>
                        ↳ Continued from chapter {l.chapterNumber} of "
                        {l.storyName}" by {l.userName}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {isTranslated && (
              <div>
                <span style={badgeStyle} aria-live="polite">
                  {t("public-reader:languageBadge.translated", {
                    language: activeLangNative,
                  })}
                </span>
                <button
                  type="button"
                  style={restoreOriginalBtn}
                  onClick={restoreOriginal}
                >
                  {t("public-reader:languageBadge.readOriginal", {
                    originalLanguage: originalLangNative,
                  })}
                </button>
              </div>
            )}

            {activeChapter ? (
              <>
                <div style={chapterLabel}>
                  chapter {activeChapter.chapterNumber}
                </div>
                <h2 style={chapterTitle}>
                  {activeBody?.title ?? activeChapter.title}
                </h2>
                {activeBody ? (
                  renderMarkdownBody(activeBody.bodyMd, fontFamily)
                ) : (
                  <div style={translatingNote} role="status" aria-live="polite">
                    {chapterLoadingId === activeChapter.id
                      ? t("public-reader:translating.chapter", {
                          n: activeChapter.chapterNumber,
                          total: book.chapters.length,
                        })
                      : t("public-reader:translateBanner.translating")}
                  </div>
                )}

                <div style={actions}>
                  <ForkAction
                    slug={slug}
                    chapterNumber={activeChapter.chapterNumber}
                    allowContinuation={book.allowContinuation}
                    authorUserId={book.author.id}
                    accent={accent}
                    accentMuted={accentMuted}
                    ink={ink}
                    inkMuted={inkMuted}
                    paperEdge={paperEdge}
                  />
                  <button type="button" style={secondaryAction} onClick={openShare}>
                    Share
                  </button>
                  {book.allowEpub &&
                    (isTranslated ? (
                      <button
                        type="button"
                        style={secondaryAction}
                        onClick={() => {
                          void downloadEpub();
                        }}
                        disabled={epubProgress !== null}
                      >
                        Download EPUB
                      </button>
                    ) : (
                      <a
                        href={buildEpubUrl(slug)}
                        style={secondaryAction}
                        download
                      >
                        Download EPUB
                      </a>
                    ))}
                </div>
              </>
            ) : (
              <div style={emptyState}>
                This story is brand new — chapters appear as the player
                plays.
              </div>
            )}
          </div>
          <PublicFooter
            accent={accent}
            inkMuted={inkMuted}
            paperEdge={paperEdge}
            paper={paper}
          />
        </main>
      </div>

      {shareOpen && (
        <ShareSheet
          shareUrl={shareUrl}
          title={book.title}
          authorName={book.author.name}
          scenarioName={book.scenario.name}
          accent={accent}
          accentMuted={accentMuted}
          ink={ink}
          inkMuted={inkMuted}
          paper={paper}
          paperEdge={paperEdge}
          onClose={closeShare}
        />
      )}
    </div>
  );
}
